import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../../layout/default';
import { isMobile, isTablet } from 'react-device-detect';
import Header from '../components/Header';
import './styles.scss';
import cx from 'classnames';
import Select from 'react-select';
import { useAuth } from '../../../container/authContext';
import {
  getAvailableFacilitiesService,
  getScheduleDataService,
  getMasterScheduleAvailableUsersService,
  addUserToScheduleService,
  deleteUserFromScheduleService,
} from '../../../services/scheduling';
import { isResponseOk } from '../../../utility/isResponseOk';
import MasterScheduleTable from './components/table';
import { getRoleLabelsService } from '../../../services/company';
import EditSidebar from './components/EditSidebar';
import dayjs from 'dayjs';
import { localToUTCDate } from '../../../utility/dates/localToUTCDate';

const MasterSchedule = () => {
  const initialFormData = {
    halfPart: { value: 'both_halves', label: 'Full shift' },
    addFromDate: null,
    notes: '',
    fontColor: '#101828',
    cellColor: '#ffffff',
    deleteOption: { value: 'all_repeated_and_single_shifts', label: 'Delete all repeated and single shifts' },
    effectiveDate: null,
  };
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [filtersData, setFiltersData] = useState({
    facility: null,
    showNames: false,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedCells, setSelectedCells] = useState([]);
  const [selectedAssignmentsToDelete, setSelectedAssignmentsToDelete] = useState([]);
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [sidebarAction, setSidebarAction] = useState(null);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const { user = {} } = useAuth();
  const { company: { id: companyID } = {} } = user;
  const [roleLabels, setRoleLabels] = useState({});

  const facilityId = useMemo(() => {
    return new URLSearchParams(window.location.search).get('facility_id');
  }, []);

  useEffect(() => {
    if (!isEditSidebarOpen) {
      setFormData(initialFormData);
      setSelectedCells([]);
      setSelectedUser(null);
      setSidebarAction(null);
    }
  }, [isEditSidebarOpen]);

  useEffect(() => {
    getRoleLabelsService(user?.company?.id).then((res) => {
      setRoleLabels(res.data);
    });
  }, [user?.company?.id]);

  const getAvailableUsers = useCallback(async () => {
    if (filtersData.facility?.value) {
      try {
        const params = {
          facility_id: filtersData.facility.value,
        };
        const response = await getMasterScheduleAvailableUsersService(params);
        if (isResponseOk(response) && response.data) {
          setAvailableUsers(response.data.map((user) => ({ ...user, value: user.id, label: user.full_name })));
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [filtersData.facility?.value]);

  useEffect(() => {
    getAvailableUsers();
  }, [getAvailableUsers]);

  useEffect(() => {
    (async () => {
      try {
        const params = {
          company_id: companyID,
          only_existing_periods: true,
        };
        const facilitiesResponse = await getAvailableFacilitiesService(params);
        if (isResponseOk(facilitiesResponse) && facilitiesResponse.data) {
          setFacilityOptions(facilitiesResponse.data.map((facility) => ({ value: facility.id, label: facility.name })));
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyID, user.id]);

  useEffect(() => {
    if (facilityId && facilityOptions?.length) {
      setFiltersData((prevState) => ({
        ...prevState,
        facility: facilityOptions.find((item) => item.value === parseInt(facilityId)),
      }));
    }
  }, [facilityOptions, facilityId]);

  const getScheduleData = useCallback(async () => {
    if (filtersData.facility?.value) {
      const scheduleDataResponse = await getScheduleDataService(filtersData.facility.value);
      if (isResponseOk(scheduleDataResponse) && scheduleDataResponse.data) {
        setScheduleData(scheduleDataResponse.data);
        const url = new URL(window.location.href);
        url.searchParams.set('facility_id', filtersData.facility.value);

        window.history.replaceState({}, '', url);
      }
    }
  }, [filtersData.facility?.value]);

  useEffect(() => {
    getScheduleData();
  }, [getScheduleData]);

  useEffect(() => {
    setSelectedCells([]);
  }, [formData.halfPart.value]);

  useEffect(() => {
    setSelectedAssignmentsToDelete([]);
  }, [formData.deleteOption?.value]);

  const handleSave = useCallback(async () => {
    try {
      setIsSavingUser(true);
      const params = {
        facility_id: filtersData.facility.value,
      };

      const payload = {
        user_id: selectedUser.id,
        half_type: formData.halfPart.value,
        start_date: dayjs(formData.addFromDate).format('YYYY-MM-DD'),
        notes: formData.notes,
        font_color: formData.fontColor,
        cell_color: formData.cellColor,
        scheduling_cell_ids: selectedCells.map((item) => item.id),
      };

      const response = await addUserToScheduleService(payload, params);
      if (isResponseOk(response)) {
        setIsEditSidebarOpen(false);
        getScheduleData();
      }
    } catch (e) {
      console.error(e);
    }
    setIsSavingUser(false);
  }, [filtersData.facility?.value, selectedUser, formData, selectedCells]);

  const handleDelete = useCallback(async () => {
    try {
      const params = {
        facility_id: filtersData.facility.value,
      };

      const payload = {
        assignment_ids: selectedAssignmentsToDelete,
        user_id: selectedUser.id,
        delete_option: formData.deleteOption.value,
        effective_date: localToUTCDate(formData.effectiveDate).toISOString().slice(0, 10),
      };

      const response = await deleteUserFromScheduleService(payload, params);
      if (isResponseOk(response)) {
        setIsEditSidebarOpen(false);
        getScheduleData();
      }
    } catch (e) {
      console.error(e);
    }
  }, [filtersData.facility?.value, selectedAssignmentsToDelete, selectedUser, formData]);

  return (
    <Layout
      title="Scheduling"
      className={cx('master-schedule-layout', {
        isTablet,
        isMobile,
      })}
      breadcrumbs={[
        {
          name: 'Scheduling',
        },
      ]}
    >
      <div className="page-wrapper master-schedule-page">
        <Header />

        <div className="library-filters">
          <div className="library-filters-wrapper" style={{ alignItems: 'flex-end', maxWidth: filtersData.showNames ? 'unset' : '1530px' }}>
            <div className="form-item">
              <label className="form-label">Facility</label>
              <Select
                options={facilityOptions}
                value={filtersData.facility}
                className="form-control-select"
                classNamePrefix="react-select"
                onChange={(e) => {
                  setFiltersData((prevState) => ({ ...prevState, facility: e }));
                }}
                placeholder="Select Facility"
                isSearchable={false}
              />
            </div>
            <div className="form-item show-names-filter">
              <div className="form-control-checkbox mt-2">
                <input
                  name="show-names"
                  type="checkbox"
                  checked={filtersData.showNames}
                  onChange={() => setFiltersData((prevState) => ({ ...prevState, showNames: !prevState.showNames }))}
                />
                <span>Show names</span>
              </div>
            </div>
            <div className="form-item" style={{ marginLeft: 'auto', width: 'unset' }}>
              <button className="btn btn-outline edit-schedule-btn" onClick={() => setIsEditSidebarOpen(true)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_15418_7572)">
                    <path
                      d="M9.1665 3.3332H3.33317C2.89114 3.3332 2.46722 3.50879 2.15466 3.82135C1.8421 4.13391 1.6665 4.55784 1.6665 4.99986V16.6665C1.6665 17.1086 1.8421 17.5325 2.15466 17.845C2.46722 18.1576 2.89114 18.3332 3.33317 18.3332H14.9998C15.4419 18.3332 15.8658 18.1576 16.1783 17.845C16.4909 17.5325 16.6665 17.1086 16.6665 16.6665V10.8332M15.4165 2.0832C15.748 1.75168 16.1977 1.56543 16.6665 1.56543C17.1353 1.56543 17.585 1.75168 17.9165 2.0832C18.248 2.41472 18.4343 2.86436 18.4343 3.3332C18.4343 3.80204 18.248 4.25168 17.9165 4.5832L9.99984 12.4999L6.6665 13.3332L7.49984 9.99986L15.4165 2.0832Z"
                      stroke="#344054"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_15418_7572">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                Edit
              </button>
            </div>
          </div>
        </div>

        {scheduleData.id ? (
          <MasterScheduleTable
            scheduleData={scheduleData}
            getScheduleData={getScheduleData}
            roleLabels={roleLabels}
            selectedCells={selectedCells}
            setSelectedCells={setSelectedCells}
            sidebarAction={sidebarAction}
            selectedUser={selectedUser}
            formData={formData}
            showNames={filtersData.showNames}
            selectedAssignmentsToDelete={selectedAssignmentsToDelete}
            setSelectedAssignmentsToDelete={setSelectedAssignmentsToDelete}
          />
        ) : (
          <div className="empty-scheduling-page">Please select a facility to see scheduling details</div>
        )}
      </div>

      {isEditSidebarOpen && (
        <EditSidebar
          setIsEditSidebarOpen={setIsEditSidebarOpen}
          availableUsers={availableUsers}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          sidebarAction={sidebarAction}
          setSidebarAction={setSidebarAction}
          formData={formData}
          selectedCells={selectedCells}
          setFormData={setFormData}
          selectedAssignmentsToDelete={selectedAssignmentsToDelete}
          handleSave={handleSave}
          isSavingUser={isSavingUser}
          handleDelete={handleDelete}
        />
      )}
    </Layout>
  );
};

export default MasterSchedule;
